"use client";

/**
 * ToolThinkingDisplay - Compact inline status line for tool calls
 *
 * Replaces verbose tool cards when debug mode is OFF.
 * Shows: spinner (executing) / checkmark (complete) / error icon + summary text
 */

import { Spinner } from "@/ui";
import { colors, spacing, typography } from "@/ui/tokens";
import { getToolThinkingSummary } from "@/lib/sola/utils/toolThinkingSummary";
import type { AggregatedToolCall } from "@/lib/sola/types/tools";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

interface ToolThinkingDisplayProps {
  toolCall: AggregatedToolCall;
}

export function ToolThinkingDisplay({ toolCall }: ToolThinkingDisplayProps) {
  const { toolName, state, input } = toolCall;
  const summary = getToolThinkingSummary(toolName, input);

  const isComplete =
    state === "output-available" &&
    toolCall.finalOutput !== undefined;
  const isError = state === "output-error" || state === "output-denied";
  const isExecuting = !isComplete && !isError;

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: spacing[2],
        paddingTop: 2,
        paddingBottom: 2,
        minHeight: 24,
      }}
    >
      {/* Status icon */}
      {isExecuting && <Spinner size="xs" color="current" />}
      {isComplete && (
        <CheckCircleOutlineIcon
          sx={{ fontSize: 16, color: colors.signal.success }}
        />
      )}
      {isError && (
        <ErrorOutlineIcon
          sx={{ fontSize: 16, color: colors.signal.danger }}
        />
      )}

      {/* Summary text */}
      <span
        style={{
          fontSize: typography.scale.sm,
          color: colors.text.muted,
          lineHeight: 1.4,
        }}
      >
        {summary}
      </span>
    </div>
  );
}
